import React, { useState, useRef, useContext, useEffect } from "react";
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import "./product-detail-description-style.scss";
import { DeviceContext, deviceType } from '@providers';

import Button from "@src/componentsNew/Buttons/Button";
import Typography from "@src/componentsNew/Typography/Typography";
import ProgressiveImg from "@src/componentsNew/Image/ProgressiveImg";
import SectionDivider from "@src/componentsNew/SectionDivider";

import ExpandMoreIcon from "@src/componentsNew/Icons/Icons/ExpandMoreIcon";

import { product_product_descriptionImages, product_product_descriptions, product_product_seller } from "@components/ProductPage/ProductDetailPage/__generated__/product";
import { replaceUnderscoreWithSpace, capitalizeFirstWord } from "@src/utils";

export interface IProps {
  descriptionImages: (product_product_descriptionImages | null)[] | null;
  descriptions: (product_product_descriptions | null)[] | null;
  countryOfOrigin: string;
  seller: product_product_seller | null;
  extraInformation: string;
}

const ProductDetailDescription = ({
  descriptionImages,
  descriptions,
  countryOfOrigin,
  seller,
  extraInformation
}: IProps) => {
  const { isMobile } = useContext<deviceType>(DeviceContext);
  const limitedContentHeight = isMobile ? '330' : '740';

  const [value, setValue] = useState(descriptionImages?.length ? 'productDetails' : 'description');

  const [isShowMoreBtn, setIsShowMoreBtn] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const descriptionContainerRef = useRef(null);
  const descriptionContentRef = useRef(null);

  useEffect(() => {
    const calculateDescriptionContentHeight = () => {
      if (descriptionContentRef.current) {
        setIsShowMoreBtn(descriptionContentRef.current.clientHeight >= limitedContentHeight);
      }
    };

    const timeoutId = setTimeout(calculateDescriptionContentHeight, 500);

    return () => clearTimeout(timeoutId);
  }, [descriptions, limitedContentHeight]);

  const scrollToDescriptionTop = () => {
    if (descriptionContainerRef.current) {
      const elementPosition = descriptionContainerRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition - 65;

      window.scrollBy({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const toggleExpand = () => {
    if (isExpanded) {
      scrollToDescriptionTop();
    }

    setIsExpanded(prev => !prev);
  };

  return (
    (descriptionImages?.length || descriptions?.length) ?
      <>
        <div id="product-detail-description-area" className={isMobile ? 'mobile' : 'desktop'} ref={descriptionContainerRef}>
          <Tabs
            className="product-detail-description-tabs"
            value={value}
            onChange={handleChange}
            variant="scrollable"
            textColor="secondary"
            indicatorColor="secondary"
            aria-label="secondary tabs example">
            {
              // descriptionImages가 존재하는 경우, 탭 가장 앞에 추가
              descriptionImages?.length &&
              <Tab
                className={`product-detail-description-tab-item ${value === "productDetails" ? 'selected' : ''}`}
                key="productDetails"
                value="productDetails"
                label="Product Details" />
            }
            {
              descriptions?.length &&
              <Tab
                className={`product-detail-description-tab-item ${value === "description" ? 'selected' : ''}`}
                key="description"
                value="description"
                label="Description" />
            }
          </Tabs>

          <div
            ref={descriptionContentRef}
            className={`selected-tab-description ${isMobile ? 'mobile' : 'desktop'} ${isExpanded ? 'expanded' : ''}`}
            style={{ maxHeight: isExpanded ? 'none' : `${limitedContentHeight}px` }}
          >
            {
              value === 'productDetails' &&
              <div className="product-description-image-wrapper">
                {
                  descriptionImages?.map(image =>
                    <ProgressiveImg key={image.id} originImg={isMobile ? image.mobileOriginImages : image.url} placeholder={image.placeholderImages} alt={image.alt} />
                  )
                }
              </div>
            }
            {
              value === 'description' &&
              <>
                <div className="product-description-wrapper">
                  {
                    descriptions?.map((description, index) =>
                      <div className="product-description-item" key={index}>
                        <Typography variant="subtitle1" component="h4" style={{ color: "#090A0FDE" }}>
                          {capitalizeFirstWord(replaceUnderscoreWithSpace(description.key))}
                        </Typography>

                        <div className="html-area" dangerouslySetInnerHTML={{ __html: description.description }} />
                      </div>
                    )
                  }
                </div>
                <div className="additional-information-wrapper">
                  {
                    countryOfOrigin &&
                    <div className="additional-information">
                      <Typography cName="additional-information-title" variant="subtitle2" component="span" style={{ color: "#666666" }}>
                        Country of Origin
                      </Typography>
                      <Typography cName="additional-information-content" variant="body3" component="span" style={{ color: "#666666" }}>
                        {countryOfOrigin}
                      </Typography>
                    </div>
                  }
                  {
                    seller?.name &&
                    <div className="additional-information">
                      <Typography cName="additional-information-title" variant="subtitle2" component="span" style={{ color: "#666666" }}>
                        Seller
                      </Typography>
                      <Typography cName="additional-information-content" variant="body3" component="span" style={{ color: "#666666" }}>
                        {seller.name}
                      </Typography>
                    </div>
                  }
                  {
                    seller?.address &&
                    <div className="additional-information">
                      <Typography cName="additional-information-title" variant="subtitle2" component="span" style={{ color: "#666666" }}>
                        Address
                      </Typography>
                      <Typography cName="additional-information-content" variant="body3" component="span" style={{ color: "#666666" }}>
                        {seller.address}
                      </Typography>
                    </div>
                  }
                  {
                    extraInformation &&
                    <div className="additional-information">
                      <Typography cName="additional-information-title" variant="subtitle2" component="span" style={{ color: "#666666" }}>
                        Extra Information
                      </Typography>
                      <Typography cName="additional-information-content extra-info" variant="body3" component="span" style={{ color: "#666666" }}>
                        {extraInformation}
                      </Typography>
                    </div>
                  }
                </div>
              </>
            }

            {
              isShowMoreBtn &&
              <>
                <Button
                  size="small"
                  cName="see-all-description-toggle-btn"
                  variant="text"
                  endIcon={<ExpandMoreIcon cName={`expand-icon ${isExpanded ? 'less' : 'more'}`} strokeColor="#222222" />}
                  onClick={toggleExpand}>
                  {isExpanded ? 'Close' : 'See All'}
                </Button>
                {!isExpanded && <div className="blur-overlay" />}
              </>
            }
          </div>
        </div>

        {isMobile ? <SectionDivider /> : null}
      </>
      : null
  )
};

export default ProductDetailDescription;
